import { lazy, type ComponentType, type LazyExoticComponent } from 'react';

type Loader<P> = () => Promise<{ default: ComponentType<P> }>;

export type LazyRoute<P = object> = ComponentType<P> & {
  preload: () => Promise<ComponentType<P>>;
};

/**
 * A route component whose code is split into its own chunk.
 * Once `preload()` has resolved, it renders synchronously (no Suspense fallback), so the
 * server render and the client's first render match exactly and hydration never suspends.
 */
export function lazyRoute<P extends object>(load: Loader<P>): LazyRoute<P> {
  let loaded: ComponentType<P> | undefined;
  let pending: Promise<ComponentType<P>> | undefined;
  const preload = () =>
    (pending ??= load().then((m) => {
      loaded = m.default;
      return loaded;
    }));
  const Lazy: LazyExoticComponent<ComponentType<P>> = lazy(() => preload().then((c) => ({ default: c })));

  const Route = (props: P) => {
    const Component = loaded ?? Lazy;
    return <Component {...props} />;
  };
  Route.preload = preload;
  return Route as LazyRoute<P>;
}
